import { useCallback } from 'react';
import type { ListActivePaymentDto } from '../../generated-api';
import { PaymentState } from '../../generated-api';
import { useToast } from '@ordr-as/umami-react/toast';
import { useLocation } from '../context/LocationContext';
import { useMutateCancelAll, useMutateCancelAppSwitch, useMutateCancelPayment } from './usePaymentCancelledApi';
import { getPaymentMethodById } from '../context/PaymentMethodStore';
import { useTerminalStore } from '../context/TerminalStore';
import useIntl from './useIntl';

interface CancelPaymentArgs {
  focusedPayment: ListActivePaymentDto;
  removeActivePayment: (payment: ListActivePaymentDto) => void;
  onDone: () => void;
}

export function useCancelPayment() {
  const { location } = useLocation();
  const cancelLegacy = useMutateCancelPayment();
  const cancelAll = useMutateCancelAll();
  const cancelAppSwitch = useMutateCancelAppSwitch();
  const toasts = useToast();
  const intl = useIntl();

  const cancelPayment = useCallback(
    ({ focusedPayment, removeActivePayment, onDone }: CancelPaymentArgs) => {
      if (!focusedPayment.paymentReference) {
        return;
      }

      const isCancelledPayment = focusedPayment.paymentState === PaymentState.FAILED;

      toasts.remove(focusedPayment.paymentReference);

      if (!isCancelledPayment) {
        const pm = getPaymentMethodById(focusedPayment.paymentMethod.id);
        const isUsingHub = pm?.isUsingHub || false;
        const isDirectSaleTerminal =
          (pm?.provider === 'ADYEN' || pm?.provider === 'NETS') &&
          focusedPayment.paymentMethod.paymentMethod === 'INTEGRATED_TERMINAL';

        if (isUsingHub) {
          cancelAppSwitch.mutate({
            data: { terminalId: focusedPayment.paymentMethod.id },
            locationId: location.id,
            paymentReference: focusedPayment.paymentReference,
          });
        } else if (isDirectSaleTerminal) {
          cancelAll.mutate({
            data: { terminalId: focusedPayment.paymentMethod.id },
            locationId: location.id,
            paymentReference: focusedPayment.paymentReference,
          });
        } else {
          cancelLegacy.mutate({
            cancelPaymentDto: {
              isUsingHub: false,
              terminalId: focusedPayment.paymentMethod.id,
            },
            locationId: location.locationId,
            paymentReference: focusedPayment.paymentReference,
          });
        }

        toasts.pushSuccess({ id: 'payment-cancelled', message: intl.formatMessage({ id: 'payment.cancelledToast' }) });
      }

      useTerminalStore.getState().actions.clearPayment(focusedPayment.paymentReference);
      removeActivePayment(focusedPayment);
      onDone();
    },
    [cancelAll, cancelAppSwitch, cancelLegacy, intl, location.id, location.locationId, toasts]
  );

  return cancelPayment;
}
